import React, { useState, useEffect, useRef } from "react";
import { X, Mail, Lock, User, ArrowRight, RefreshCw, Gift, Sparkles, Loader2, CheckCircle } from "lucide-react";
import { SignUpData, SignInData } from "../types";
import { supabase } from "../config/supabase";
import {
  canResendNow,
  resendSuccessMessage,
  sanitizeResendError,
  secondsUntilResend,
} from "../utils/confirmationResend";

interface AuthModalProps {
  isOpen: boolean;
  mode: "signin" | "signup";
  onClose: () => void;
  onSwitchMode: () => void;
  onSignIn: (data: SignInData) => Promise<void>;
  onSignUp: (data: SignUpData) => Promise<void>;
}

// Premium Input Component
const PremiumInput: React.FC<{
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  icon: React.ReactNode;
  required?: boolean;
  maxLength?: number;
  hint?: string;
  autoFocus?: boolean;
}> = ({ label, type, value, onChange, placeholder, icon, required, maxLength, hint, autoFocus }) => (
  <div className="space-y-2">
    <label className="block text-sm font-semibold text-gray-700">
      {label}
      {!required && <span className="text-gray-400 font-normal ml-1">(optional)</span>}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none text-gray-400">
        {icon}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(type === 'text' && maxLength ? e.target.value.toUpperCase() : e.target.value)}
        className="
          w-full pl-8 pr-4 py-3.5
          bg-white/80 backdrop-blur-sm
          border-2 border-gray-200 rounded-xl
          text-gray-900 placeholder-gray-400
          focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10
          hover:border-gray-300
          transition-all duration-200
          outline-none
        "
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        autoFocus={autoFocus}
      />
    </div>
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);

// Alert Component
const Alert: React.FC<{
  variant: 'error' | 'success' | 'info';
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ variant, children, action }) => {
  const styles = {
    error: 'bg-rose-50 border-rose-200 text-rose-700',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    info: 'bg-blue-50 border-blue-200 text-blue-700',
  };
  
  return (
    <div className={`p-4 rounded-xl border-2 ${styles[variant]} animate-fade-in-up`}>
      <p className="text-sm font-medium">{children}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
};

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  mode,
  onClose,
  onSwitchMode,
  onSignIn,
  onSignUp,
}) => {
  const [formData, setFormData] = useState<SignUpData | SignInData>(() => {
    return mode === "signup"
      ? { email: "", password: "", firstName: "", lastName: "", referralCode: "" }
      : { email: "", password: "" };
  });

  useEffect(() => {
    if (mode === "signup") {
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get("ref");
      if (refCode) {
        setFormData((prev) => ({ ...prev, referralCode: refCode.toUpperCase() }));
      }
    }
  }, [mode]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResendOption, setShowResendOption] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [lastResendAt, setLastResendAt] = useState<number | null>(null);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen && submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
    }
    // signUp/signIn return fast now that profile/referral work is off the hot path
    const authTimeoutMs = 45000;
    submitTimeoutRef.current = setTimeout(() => {
      submitTimeoutRef.current = null;
      setIsLoading(false);
      setError(
        mode === "signup"
          ? "Sign-up is taking unusually long. Check your connection and try again."
          : "Sign-in is taking unusually long. Check your connection and try again. If you are sure your password is correct, wait a minute — the server may be slow — or try Forgot password if you recently changed it."
      );
    }, authTimeoutMs);

    try {
      if (mode === "signup") {
        const signupData = formData as SignUpData;
        if (!signupData.firstName || !signupData.lastName) {
          throw new Error("Please fill in all fields");
        }
        if (!acceptedPolicies) {
          throw new Error("Please accept the Privacy Policy and Terms of Service.");
        }
        await onSignUp(signupData);
      } else {
        const d = formData as SignInData;
        await onSignIn({ email: d.email.trim(), password: d.password });
      }
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
      onClose();
    } catch (error) {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
      const errorMessage = error instanceof Error ? error.message : `${mode === "signup" ? "Sign up" : "Sign in"} failed`;
      setError(errorMessage);
      if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('confirm')) {
        setShowResendOption(true);
      }
    } finally {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    const email = (formData as SignInData).email?.trim();
    if (!email) {
      setError('Enter the email address you used to sign up, then try again.');
      return;
    }
    if (!canResendNow(lastResendAt)) {
      setError(`Please wait ${secondsUntilResend(lastResendAt)} seconds before requesting another email.`);
      return;
    }
    setIsLoading(true);
    setResendSuccess(false);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      setLastResendAt(Date.now());
      if (error) {
        setError(sanitizeResendError(error.message));
        return;
      }
      setResendSuccess(true);
      setError(null);
      setShowResendOption(false);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      setLastResendAt(Date.now());
      setError(sanitizeResendError(error instanceof Error ? error.message : ''));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Please enter your email address first');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setForgotPasswordSuccess(false);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setForgotPasswordSuccess(true);
      setTimeout(() => setForgotPasswordSuccess(false), 10000);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send password reset email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchMode = () => {
    const currentEmail = 'email' in formData ? formData.email : "";
    onSwitchMode();
    setFormData(
      mode === "signup"
        ? { email: currentEmail, password: "" }
        : { email: currentEmail, password: "", firstName: "", lastName: "", referralCode: "" }
    );
    setError(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-modal-in">
        {/* Header with gradient */}
        <div className="relative bg-gradient-to-r from-slate-900 via-indigo-900 to-purple-900 text-white overflow-hidden">
          {/* Pattern overlay */}
          <div className="absolute inset-0 opacity-20" style={{ 
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.15) 1px, transparent 1px)',
            backgroundSize: '24px 24px'
          }} />
          
          <div className="relative p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
                  <Sparkles size={20} className="text-amber-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">
                    {mode === "signup" ? "Join ClubIQ" : "Welcome Back"}
                  </h2>
                  <p className="text-indigo-200 text-sm">
                    {mode === "signup" ? "Create your free account" : "Sign in to continue"}
                  </p>
                </div>
              </div>
              
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-white/10 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error Alert */}
            {error && (
              <Alert 
                variant="error"
                action={showResendOption && (
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={isLoading}
                    className="
                      w-full flex items-center justify-center gap-2 
                      px-4 py-2.5 
                      bg-gradient-to-r from-indigo-600 to-purple-600 text-white 
                      rounded-xl font-semibold text-sm
                      hover:from-indigo-700 hover:to-purple-700 
                      transition-all disabled:opacity-50
                      shadow-lg shadow-indigo-500/20
                    "
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    Resend Confirmation Email
                  </button>
                )}
              >
                {error}
              </Alert>
            )}
            
            {/* Success Messages */}
            {resendSuccess && (
              <Alert variant="success">
                <span className="flex items-center gap-2">
                  <CheckCircle size={16} />
                  {resendSuccessMessage()}
                </span>
              </Alert>
            )}
            
            {forgotPasswordSuccess && (
              <Alert variant="info">
                📧 Password reset email sent! Check your inbox for the reset link.
              </Alert>
            )}

            {/* Sign Up Fields */}
            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-4">
                <PremiumInput
                  label="First Name"
                  type="text"
                  value={'firstName' in formData ? formData.firstName : ""}
                  onChange={(v) => handleInputChange("firstName", v)}
                  placeholder="John"
                  icon={<User size={18} />}
                  required
                  autoFocus
                />
                <PremiumInput
                  label="Last Name"
                  type="text"
                  value={'lastName' in formData ? formData.lastName : ""}
                  onChange={(v) => handleInputChange("lastName", v)}
                  placeholder="Doe"
                  icon={<User size={18} />}
                  required
                />
              </div>
            )}

            {/* Email */}
            <PremiumInput
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(v) => handleInputChange("email", v)}
              placeholder="you@example.com"
              icon={<Mail size={18} />}
              required
              autoFocus={mode === "signin"}
            />

            {/* Password */}
            <div className="space-y-2">
              <PremiumInput
                label="Password"
                type="password"
                value={formData.password}
                onChange={(v) => handleInputChange("password", v)}
                placeholder={mode === "signup" ? "Min. 6 characters" : "Enter password"}
                icon={<Lock size={18} />}
                required
              />
              {mode === "signin" && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={isLoading}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            {/* Referral Code */}
            {mode === "signup" && (
              <PremiumInput
                label="Referral Code"
                type="text"
                value={'referralCode' in formData ? formData.referralCode || "" : ""}
                onChange={(v) => handleInputChange("referralCode", v)}
                placeholder="e.g., DOUG7X3K"
                icon={<Gift size={18} />}
                maxLength={8}
                hint="Have a friend's code? Get bonus entries!"
              />
            )}

            {mode === "signup" && (
              <label className="flex items-start gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={acceptedPolicies}
                  onChange={(event) => setAcceptedPolicies(event.target.checked)}
                  className="mt-1"
                  required
                />
                <span>
                  I agree to the{' '}
                  <a href="/privacy" className="text-indigo-600 font-semibold underline" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                  {' '}and{' '}
                  <a href="/terms" className="text-indigo-600 font-semibold underline" target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>
                  . ClubIQ collects account and golf profile data to run the app.
                </span>
              </label>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || (mode === "signup" && !acceptedPolicies)}
              className="
                w-full flex items-center justify-center gap-2
                bg-gradient-to-r from-indigo-600 to-purple-600 text-white 
                py-4 px-6 rounded-xl font-semibold
                shadow-lg shadow-indigo-500/30
                hover:shadow-xl hover:shadow-indigo-500/40
                hover:-translate-y-0.5
                active:translate-y-0
                disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0
                transition-all duration-200
              "
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {mode === "signup" ? "Creating Account..." : "Signing In..."}
                </>
              ) : (
                <>
                  {mode === "signup" ? "Create Free Account" : "Sign In"}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Early Adopter Badge */}
          <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
            <p className="text-sm text-emerald-800 text-center font-medium flex items-center justify-center gap-2">
              <Sparkles size={16} className="text-emerald-600" />
              First 1,000 users get free access forever!
            </p>
          </div>

          {/* Switch Mode */}
          <div className="text-center pt-2">
            <p className="text-gray-500 text-sm">
              {mode === "signup" ? "Already have an account?" : "Don't have an account?"}{" "}
              <button
                type="button"
                onClick={handleSwitchMode}
                className="text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
              >
                {mode === "signup" ? "Sign in" : "Sign up free"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
